"use client";

import { useState } from "react";
import Button from "@/components/ui/Button";
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { applyPhoneMask } from "@/lib/utils/form-masks";

export default function ContatoPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Aplica máscara de telefone
    if (name === "phone") {
      setFormData({
        ...formData,
        [name]: applyPhoneMask(value),
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({
          type: "success",
          message:
            "Mensagem enviada com sucesso! Entraremos em contato em breve.",
        });
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setStatus({
          type: "error",
          message: data.error || "Erro ao enviar mensagem. Tente novamente.",
        });
      }
    } catch {
      setStatus({
        type: "error",
        message:
          "Erro ao enviar mensagem. Verifique sua conexão e tente novamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-mygreen py-16 md:py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Entre em Contato
            </h1>
            <p className="text-lg md:text-xl opacity-95">
              Estamos aqui para ajudar! Envie sua mensagem e responderemos o
              mais rápido possível.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-mygreen/10 flex items-center justify-center mb-4">
                  <EnvelopeIcon className="w-6 h-6 text-mygreen" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Email</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Envie-nos um email a qualquer momento
                </p>
                <a
                  href="mailto:contato@santafeconecta.com.br"
                  className="text-mygreen hover:text-mygreen/80 font-medium text-sm"
                >
                  contato@santafeconecta.com.br
                </a>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-mygreen/10 flex items-center justify-center mb-4">
                  <PhoneIcon className="w-6 h-6 text-mygreen" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Telefone</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Segunda a Sexta das 8h às 18h
                </p>
                <a
                  href="tel:+551799718095"
                  className="text-mygreen hover:text-mygreen/80 font-medium text-sm"
                >
                  (17) 99719-8095
                </a>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-mygreen/10 flex items-center justify-center mb-4">
                  <MapPinIcon className="w-6 h-6 text-mygreen" />
                </div>
                <h3 className="font-semibold text-lg mb-2">Localização</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Visite-nos pessoalmente
                </p>
                <p className="text-gray-800 text-sm">
                  Santa Fé do Sul - SP
                  <br />
                  Brasil
                </p>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <h2 className="text-2xl font-bold mb-2">Envie sua Mensagem</h2>
                <p className="text-gray-600 mb-8">
                  Preencha o formulário abaixo e entraremos em contato em breve
                </p>

                {status && (
                  <div
                    className={`mb-6 p-4 rounded-lg ${
                      status.type === "success"
                        ? "bg-green-50 text-green-800 border border-green-200"
                        : "bg-red-50 text-red-800 border border-red-200"
                    }`}
                  >
                    {status.message}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Nome completo *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-mygreen focus:ring-2 focus:ring-mygreen/20 outline-none transition-all"
                        placeholder="Seu nome"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-mygreen focus:ring-2 focus:ring-mygreen/20 outline-none transition-all"
                        placeholder="seu@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Telefone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-mygreen focus:ring-2 focus:ring-mygreen/20 outline-none transition-all"
                        placeholder="(00) 00000-0000"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Assunto *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-mygreen focus:ring-2 focus:ring-mygreen/20 outline-none transition-all"
                        placeholder="Como podemos ajudar?"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Mensagem *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-mygreen focus:ring-2 focus:ring-mygreen/20 outline-none transition-all resize-none"
                      placeholder="Escreva sua mensagem aqui..."
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      * Campos obrigatórios
                    </p>
                    <Button type="submit" variant="primary" loading={loading}>
                      {loading ? "Enviando..." : "Enviar Mensagem"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
